import React, {
  FocusEvent,
  ForwardedRef,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import classNames from 'classnames'
import { Link as ReactRouterLink } from 'react-router-dom'
import { ThemeContext } from 'styled-components'

import { getValidProps, mergeRefs } from 'hsds/utils/react'

import type { ButtonColorType, ButtonProps } from './Button.types'

export const SIZE_XXL = 'xxl'
export const SIZE_XL = 'xl'
export const SIZE_LG = 'lg'
export const SIZE_MD = 'md'
export const SIZE_SM = 'sm'
export const SIZE_XS = 'xs'
export const SIZE_XXS = 'xxs'

export const COLOR_BLUE = 'blue'
export const COLOR_RED = 'red'
export const COLOR_GREEN = 'green'
export const COLOR_GREY = 'grey'
export const COLOR_YELLOW = 'yellow'
export const COLOR_PURPLE = 'purple'
export const COLOR_AI_PURPLE = 'ai-purple'
export const COLOR_BRAND = 'brand' as const
export const COLOR_GRAY = 'gray' as const
export const COLOR_GREEN_LIGHT = 'green-light'
export const COLOR_GREY_LIGHT = 'grey-light'
export const COLOR_BLUE_LIGHT = 'blue-light'
export const COLOR_GREEN_ACCENT = 'green-accent'
export const COLOR_BLUE_ACCENT = 'blue-accent'
export const COLOR_RED_ACCENT = 'red-accent'

export const STYLE_FILLED = 'filled'
export const STYLE_LINK = 'link'
export const STYLE_OUTLINED = 'outlined'

export const SIZES = [
  SIZE_XXL,
  SIZE_XL,
  SIZE_LG,
  SIZE_MD,
  SIZE_SM,
  SIZE_XS,
  SIZE_XXS,
] as const

export const COLORS = [
  COLOR_BLUE,
  COLOR_RED,
  COLOR_GREEN,
  COLOR_GREY,
  COLOR_YELLOW,
  COLOR_PURPLE,
  COLOR_AI_PURPLE,
  COLOR_BRAND,
  COLOR_GREEN_LIGHT,
  COLOR_GREY_LIGHT,
  COLOR_BLUE_LIGHT,
  COLOR_GREEN_ACCENT,
  COLOR_BLUE_ACCENT,
  COLOR_RED_ACCENT,
] as const

type ButtonColorHookProp = {
  color?: ButtonColorType | typeof COLOR_GRAY
  theme?: string
}

const useButtonColor = ({
  color,
  theme,
}: ButtonColorHookProp): (typeof COLORS)[number] | typeof COLOR_BRAND => {
  // Guard `process` with `typeof` — a bare `process`/`process?.env` reference
  // throws "Can't find variable: process" in browsers (e.g. Storybook), and
  // the optional chaining also defeats the bundler's static replacement of
  // `process.env.NODE_ENV`.
  const isDevEnv =
    typeof process !== 'undefined' &&
    !!process.env &&
    process.env.NODE_ENV === 'development'

  if (theme && isDevEnv) {
    console.warn(
      'The Button `theme` prop has been deprecated. Please use the `color` prop instead.'
    )
  }

  const styledTheme = useContext(ThemeContext)

  // Brand color is opt-in via `color="brand"`. It reads the palette off the
  // styled-components theme (`theme.brandColor`, set by a brand ThemeProvider);
  // without one it falls back to the default `button.color.brand.*` tokens.
  if (color === COLOR_BRAND) {
    if (isDevEnv && !(styledTheme && styledTheme.brandColor)) {
      console.warn(
        'Button `color="brand"` was used without a styled-components ThemeProvider providing a `brandColor`. Falling back to the default brand token styling.'
      )
    }
    return COLOR_BRAND
  }
  if (color === 'gray') return COLOR_GREY
  if (color) return color

  return COLOR_BLUE
}

const useButtonSize = ({ size }: Pick<ButtonProps, 'size'>) => {
  if (size && SIZES.includes(size)) return size
  return SIZE_LG
}

const BUTTON_STYLES = [STYLE_FILLED, STYLE_OUTLINED, STYLE_LINK] as const

const useButtonStyle = ({
  outlined,
  linked,
}: Pick<
  ButtonProps,
  'outlined' | 'linked'
>): (typeof BUTTON_STYLES)[number] => {
  if (outlined) return STYLE_OUTLINED
  if (linked) return STYLE_LINK
  return STYLE_FILLED
}

const useButtonChildren = ({
  children,
}: PropsWithChildren<Record<never, never>>): ReactNode => {
  return useMemo(() => {
    return React.Children.map(children, child => {
      if (typeof child === 'string') {
        return <span className="c-Button__Text">{child}</span>
      }
      return child
    })
  }, [children])
}

export const useButtonClassnames = (
  defaultClassname: string,
  props: ButtonProps & {
    seamless?: boolean
    isFirst?: boolean
    isLast?: boolean
    isNotOnly?: boolean
  }
): string => {
  const {
    ariaDisabled,
    className,
    disabled,
    inlined,
    loading,
    prefixIcon,
    rounded,
    seamless,
    suffixIcon,
    isFirst,
    isLast,
    isNotOnly,
  } = props

  const color = useButtonColor(props)
  const size = useButtonSize(props)
  const style = useButtonStyle(props)

  const isDisabled = disabled || loading || ariaDisabled

  return classNames(
    defaultClassname,
    seamless && `is-seamless`,
    isDisabled && 'is-disabled',
    ariaDisabled && 'is-aria-disabled',
    loading && 'is-loading',
    rounded && 'is-rounded',
    size && `is-size-${size}`,
    style && `is-style-${style}`,
    color && `is-color-${color}`,
    inlined && 'is-inlined',
    prefixIcon && 'has-prefix-icon',
    suffixIcon && 'has-suffix-icon',
    isFirst && 'is-first',
    isLast && 'is-last',
    isNotOnly && 'is-notOnly',
    className
  )
}

export const useButtonAs = ({
  as,
  ariaDisabled,
  disabled,
  href,
  loading,
  rel,
  tabIndex,
  submit,
  target,
  to,
}: ButtonProps) => {
  const isDisabled = disabled || loading
  const type: 'submit' | 'button' = submit ? 'submit' : 'button'
  const hrefValue = href || to
  const selector = as || Boolean(hrefValue) ? 'a' : 'button'

  const tabIndexValue = isDisabled ? undefined : tabIndex || 0
  const ariaDisabledValue = isDisabled || ariaDisabled ? true : undefined

  if (selector === 'button') {
    return {
      as: to ? ReactRouterLink : selector,
      to: to ? to : undefined,
      type,
      disabled: isDisabled,
      tabIndex: tabIndexValue,
      // Native `disabled` already conveys the state, so aria-disabled is omitted.
      // The key is left out entirely when false so a consumer's own
      // `aria-disabled` prop survives the merge downstream.
      ...(!isDisabled && ariaDisabled && { 'aria-disabled': true as const }),
    }
  }

  return {
    as: to ? ReactRouterLink : selector,
    role: selector === 'a' ? 'link' : 'button',
    tabIndex: tabIndexValue,
    href:
      selector === 'a' && (isDisabled || ariaDisabled) ? undefined : hrefValue,
    to: to ? to : undefined,
    target: selector === 'a' ? target : undefined,
    ...(ariaDisabledValue && { 'aria-disabled': true as const }),
    rel: selector === 'a' ? rel : undefined,
  }
}

export const useButton = (
  props: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) => {
  const {
    as,
    ariaDisabled,
    disabled,
    href,
    loading,
    onBlur,
    onClick,
    rel,
    submit,
    shortcut,
    target,
    tabIndex,
    to,
    ...otherProps
  } = props

  const buttonRef = useRef<HTMLButtonElement>(null)
  const children = useButtonChildren(props)
  const componentClassName = useButtonClassnames('c-Button', props)

  const additionalProps = useButtonAs({
    as,
    ariaDisabled,
    disabled,
    href,
    loading,
    rel,
    submit,
    target,
    tabIndex,
    to,
  })

  const { prefixIcon, suffixIcon, ...rest } = otherProps

  const handleOnBlur = useCallback(
    (e: FocusEvent<HTMLButtonElement>) => {
      if (onBlur) onBlur(e)

      const isEventHandled =
        e && (e.isDefaultPrevented() || e.isPropagationStopped())
      if (!isEventHandled && buttonRef.current) {
        buttonRef.current.classList.remove('is-focused')
      }
    },
    [onBlur]
  )

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (ariaDisabled) {
        e.preventDefault()
        return
      }
      return onClick?.(e)
    },
    [ariaDisabled, onClick]
  )

  return {
    'data-testid': 'Button',
    ...additionalProps,
    ...getValidProps(rest),
    className: componentClassName,
    children,
    loading,
    prefixIcon,
    suffixIcon,
    shortcut,
    onBlur: handleOnBlur,
    onClick: handleOnClick,
    buttonRef: mergeRefs(ref, buttonRef),
  }
}
